// Clan-roster captain authority (T4, delegation-visibility audit) — pure
// gating helpers for LeaderPanel / ClanTeamsBoard / GhostSeatManager /
// ClanMemberRow. Mirrors the backend's team-scoped delegation overlay
// EXACTLY (canManageClanCapability + hasActiveDelegatedCapability,
// portalApi/index.ts + shared/delegation.ts, audit fix 2026-07-19): a
// captain's grant affirms ONLY when its own teamNo matches the TARGET's own
// teamNo exactly — never clan-wide, never another team.
//
// Judgement call (see report): viewerCaptainTeams is NOT derived from
// clan.activeDelegations. That field is populated by GET /clan/:name ONLY
// for callers who already pass clan.manage_roles (owner/co-admin/platform
// admin) — a plain-member captain's OWN grant never rides through it, so it
// is always [] for the exact population this feature is for. The only
// endpoint that reliably answers "is *I* a captain, and of which team" for a
// non-owner caller is GET /clans/:name/my-grant (api.myClanGrant) — already
// used by JoinCompScreen for the identical "am I an active captain" check.
// viewerCaptainTeams is an array (not a single number) so this stays correct
// if a future multi-grant lookup ever replaces the current single-grant
// my-grant endpoint — today it will only ever hold 0 or 1 entries.
//
// Run: npx vitest run src/lib/captainAccess.test.ts

/** Does the viewer captain this exact team? Unassigned/bench (teamNo null/undefined) never matches — mirrors the backend's own exclusion of unassigned targets. */
export function isCaptainOfTeam(
  viewerCaptainTeams: number[],
  teamNo: number | null | undefined,
): boolean {
  return typeof teamNo === 'number' && viewerCaptainTeams.includes(teamNo);
}

/**
 * Row/team-scoped management access: the existing clan-wide `canManage`
 * (owner/co-admin/platform-admin — UNCHANGED, always wins first) OR the
 * viewer captains this specific target's team. Used for: remove-member
 * (target = member.teamNo), ghost-seat create/claim-link/edit/remove
 * (target = seat.teamNo), applied per-row/per-team rather than as one flat
 * clan-wide boolean. Captain access is purely additive — never subtracts
 * from what canManage already grants.
 */
export function canManageTeamScoped(
  canManage: boolean,
  viewerCaptainTeams: number[],
  teamNo: number | null | undefined,
): boolean {
  return canManage || isCaptainOfTeam(viewerCaptainTeams, teamNo);
}

/**
 * Team choices for a captain's ghost-seat-create form: `null` means "no
 * restriction" (a full leader/admin picker elsewhere is fine as-is); a
 * captain who is not a clan-wide manager gets ONLY their own captained
 * team(s) — one team locks the picker outright, multiple restrict the
 * choices to those. Mirrors POST /clans/:id/seats' own per-target check
 * (seatTeamNo ?? null against the caller's grant) — a captain can never pin
 * a new seat to a team they don't run, nor to the unpinned/pool tier.
 */
export function captainSeatTeamChoices(
  canManage: boolean,
  viewerCaptainTeams: number[],
): number[] | null {
  return canManage ? null : viewerCaptainTeams;
}

// ── Own-team seat/bench assignment (captain own-team UI, 2026-07-21) ───────
//
// Mirrors the backend's PATCH /clans/:name/members/:granteeId/team overlay
// EXACTLY (portalApi/index.ts, clanMemberTeamMatch handler ~11641-11651):
//
//   if (isLeaderLike || isAdminLike) { permitted }
//   else if (DELEGATION_OVERLAY_ENABLED) {
//     if (!grant || grant.teamNo === null) 403;
//     if (requestedTeamNo !== null && requestedTeamNo !== grant.teamNo) 403;   // ASSIGN direction
//     if (requestedTeamNo === null && targetMembership.teamNo !== grant.teamNo) 403; // UNASSIGN direction
//   }
//
// Two DIFFERENT checks for the two directions — see each function's own doc.

/**
 * ASSIGN direction: can the viewer set a target member's team to
 * `destinationTeamNo`? The backend checks ONLY the destination team, never
 * the target's current team — so a captain CAN pull a member off another
 * team straight into their own (no source restriction). Mirror this
 * exactly; do not add a "target must be on the bench" narrowing here, the
 * backend doesn't enforce one.
 */
export function canAssignToTeam(
  canManage: boolean,
  viewerCaptainTeams: number[],
  destinationTeamNo: number,
): boolean {
  return canManage || viewerCaptainTeams.includes(destinationTeamNo);
}

/**
 * UNASSIGN direction: can the viewer clear a target member's team (send
 * them to the bench)? The backend checks the target's CURRENT team — same
 * condition canManageTeamScoped already encodes (a captain manages a target
 * whose team they captain), so this delegates there; kept as its own named
 * export so call sites read as "can I bench this person" rather than the
 * more general row-permission name.
 */
export function canUnassignFromTeam(
  canManage: boolean,
  viewerCaptainTeams: number[],
  currentTeamNo: number | null | undefined,
): boolean {
  return canManageTeamScoped(canManage, viewerCaptainTeams, currentTeamNo);
}
